package com.schoolconnect.app.feature.auth

import com.schoolconnect.app.datasource.network.AuthSessionStore
import com.schoolconnect.app.datasource.network.TokenStore
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.response.IdentityContextProfile
import com.schoolconnect.app.datasource.network.response.RequestOtpResponse
import com.schoolconnect.app.datasource.network.response.VerifyOtpResponse
import com.schoolconnect.app.datasource.network.response.resolveRoleCode
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.rolepicker.RolePickerOption
import com.schoolconnect.app.feature.rolepicker.RolePickerUiState
import com.schoolconnect.app.repository.auth.AuthRepository
import com.schoolconnect.app.runtime.AppErrorCenter
import com.schoolconnect.app.util.isValidEmail
import com.schoolconnect.app.util.isValidOtp
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AuthViewModel(
    private val onAuthenticated: (String, String, String) -> Unit = { _, _, _ -> }
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] AuthViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(AuthUiState())
    private var countdownJob: Job? = null

    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    init {
        restoreSavedSession()
    }

    fun onEvent(event: AuthUiEvent) {
        when (event) {
            AuthUiEvent.BackClicked -> Unit
            AuthUiEvent.SendOtpClicked -> sendOtp()
            AuthUiEvent.ResendOtpClicked -> sendOtp()
            AuthUiEvent.VerifyOtpClicked -> verifyOtp()
            AuthUiEvent.RolePickerClosed -> closeRolePicker()
            AuthUiEvent.RolePickerRequested -> requestRolePicker()
            AuthUiEvent.RolePickerContinueClicked -> continueWithSelectedRole()
            is AuthUiEvent.RolePickerSelectionChanged -> selectRole(event.optionId)
            is AuthUiEvent.RolePickerRememberChanged -> updateRoleRememberChoice(event.value)
            is AuthUiEvent.LoginIdChanged -> updateLoginId(event.value)
            is AuthUiEvent.OtpChanged -> updateOtp(event.value)
        }
    }

    private fun restoreSavedSession() {
        val session = AuthSessionStore.getSession()
        if (session == null) {
            scLog("$TAG init no saved auth session")
            return
        }

        val savedEmail = session.email.trim()
        val rolePicker = buildRolePickerState(session.response)
        _uiState.update {
            it.copy(
                loginId = savedEmail.ifBlank { it.loginId },
                infoMessage = if (savedEmail.isBlank()) "Saved session restored" else "Saved session restored for $savedEmail",
                rolePicker = rolePicker
            )
        }
        scLog("$TAG init restored session user=${session.response.userId.ifBlank { "<blank>" }} profiles=${session.response.identityContextProfiles.size}")
    }

    private fun updateLoginId(value: String) {
        val normalized = value.trim()
        val currentLoginId = _uiState.value.loginId.trim()
        _uiState.update {
            it.copy(
                loginId = normalized,
                errorMessage = "",
                infoMessage = "",
                errorNonce = it.errorNonce,
                otp = if (normalized == currentLoginId) it.otp else "",
                isOtpSent = if (normalized == currentLoginId) it.isOtpSent else false,
                isOtpVerified = if (normalized == currentLoginId) it.isOtpVerified else false,
                otpCountdownSeconds = if (normalized == currentLoginId) it.otpCountdownSeconds else 0
            )
        }
        if (normalized != currentLoginId) {
            cancelCountdown()
        }
    }

    private fun updateOtp(value: String) {
        val normalized = value.filter(Char::isDigit).take(6)
        _uiState.update { it.copy(otp = normalized, errorMessage = "") }
    }

    private fun sendOtp() {
        val state = _uiState.value
        val email = state.loginId.trim()

        if (!isValidEmail(email)) {
            emitError("Enter a valid email address")
            return
        }
        if (state.isLoading) return
        if (state.otpCountdownSeconds > 0 && state.isOtpSent) {
            emitError("You can resend OTP in ${state.otpCountdownSeconds}s")
            return
        }

        scLog("$TAG requestOtp email=$email")
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = "",
                isOtpSent = false,
                isOtpVerified = false
            )
        }

        scope.launch {
            val result = AuthRepository.requestOtp(email)
            result.fold(
                onSuccess = { response ->
                    onRequestOtpSuccess(email, response)
                },
                onFailure = { throwable ->
                    emitError(throwable.message ?: "Unable to request OTP")
                    _uiState.update { it.copy(isLoading = false) }
                    scLog("$TAG requestOtp failure email=$email", throwable)
                }
            )
        }
    }

    private fun onRequestOtpSuccess(email: String, response: RequestOtpResponse) {
        val seconds = response.resendAvailableInSeconds.coerceAtLeast(0)
        cancelCountdown()
        _uiState.update {
            it.copy(
                loginId = email,
                isLoading = false,
                errorMessage = "",
                infoMessage = response.message.ifBlank { "OTP sent to $email" },
                isOtpSent = true,
                isOtpVerified = false,
                otp = "",
                otpCountdownSeconds = seconds
            )
        }
        if (seconds > 0) {
            startCountdown(seconds)
        }
        scLog("$TAG requestOtp success email=$email resendIn=$seconds")
    }

    private fun verifyOtp() {
        val state = _uiState.value
        val email = state.loginId.trim()

        if (!isValidEmail(email)) {
            emitError("Enter a valid email address")
            return
        }
        if (!state.isOtpSent) {
            emitError("Request OTP first")
            return
        }
        if (!isValidOtp(state.otp)) {
            emitError("Enter the 6-digit OTP")
            return
        }
        if (state.isLoading) return

        scLog("$TAG verifyOtp email=$email")
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            val result = AuthRepository.verifyOtp(email, state.otp)
            result.fold(
                onSuccess = { response ->
                    onVerifyOtpSuccess(email, response)
                },
                onFailure = { throwable ->
                    emitError(throwable.message ?: "Unable to verify OTP")
                    _uiState.update { it.copy(isLoading = false) }
                    scLog("$TAG verifyOtp failure email=$email", throwable)
                }
            )
        }
    }

    private fun onVerifyOtpSuccess(email: String, response: VerifyOtpResponse) {
        if (response.identityContextProfiles.isEmpty()) {
            val message = "Login failed because no roles are assigned to this account. Please contact your administrator."
            UserSessionStore.clear()
            _uiState.update {
                it.copy(
                    isLoading = false,
                    isOtpSent = false,
                    isOtpVerified = false,
                    errorMessage = message,
                    infoMessage = "",
                    errorNonce = it.errorNonce + 1,
                    rolePicker = RolePickerUiState()
                )
            }
            AppErrorCenter.emit(message, source = TAG, dedupeKey = "login-no-role-profile")
            scLog("$TAG verifyOtp rejected user=${response.userId.ifBlank { email }} reason=no_role_profiles")
            return
        }

        val rolePicker = buildRolePickerState(response)
        val primaryContext = derivePrimaryContext(response)
        val hasSingleProfile = response.identityContextProfiles.size == 1

        AuthSessionStore.saveSession(email, response)
        TokenStore.setTokens(
            accessToken = response.accessToken,
            refreshToken = "",
            tokenType = response.tokenType
        )
        UserSessionStore.setUserId(response.userId.ifBlank { email })
        UserSessionStore.setHomeRoleCode(primaryContext.roleCode)
        UserSessionStore.setHomeRoleSchoolId(primaryContext.schoolId)
        UserSessionStore.setHomeRoleSchoolCode(primaryContext.schoolCode)
        UserSessionStore.setActiveAssignmentId(primaryContext.id)

        cancelCountdown()
        _uiState.update {
            it.copy(
                isLoading = false,
                errorMessage = "",
                infoMessage = "OTP verified successfully",
                isOtpVerified = true,
                otpCountdownSeconds = 0,
                rolePicker = rolePicker.copy(
                    isVisible = !hasSingleProfile,
                    canDismiss = !hasSingleProfile
                )
            )
        }

        if (hasSingleProfile) {
            val userId = response.userId.ifBlank { email }
            scLog(
                "$TAG verifyOtp auto-selecting single profile role=${primaryContext.roleCode} school=${primaryContext.schoolId.ifBlank { "<blank>" }}"
            )
            onAuthenticated(userId, primaryContext.roleCode, primaryContext.schoolId)
        }

        scLog(
            "$TAG verifyOtp success user=${response.userId.ifBlank { email }} profiles=${response.identityContextProfiles.size} role=${primaryContext.roleCode}"
        )
    }

    private fun selectRole(optionId: String) {
        _uiState.update {
            it.copy(
                rolePicker = it.rolePicker.copy(selectedOptionId = optionId)
            )
        }
    }

    private fun updateRoleRememberChoice(value: Boolean) {
        _uiState.update {
            it.copy(
                rolePicker = it.rolePicker.copy(rememberSelection = value)
            )
        }
    }

    private fun closeRolePicker() {
        _uiState.update {
            it.copy(rolePicker = it.rolePicker.copy(isVisible = false))
        }
    }

    private fun requestRolePicker() {
        val restored = AuthSessionStore.getSession()?.response
        val rolePicker = if (restored != null) {
            buildRolePickerState(restored)
        } else {
            defaultRolePickerState()
        }
        _uiState.update {
            it.copy(
                rolePicker = rolePicker.copy(
                    isVisible = true,
                    canDismiss = false
                )
            )
        }
    }

    private fun continueWithSelectedRole() {
        val state = _uiState.value
        val selectedOption = state.rolePicker.options.firstOrNull { it.id == state.rolePicker.selectedOptionId }
            ?: state.rolePicker.options.firstOrNull()
            ?: return

        val userId = UserSessionStore.getUserId()
            .ifBlank { AuthSessionStore.getSession()?.response?.userId.orEmpty() }
            .ifBlank { state.loginId.ifBlank { "local-user" } }
        UserSessionStore.setUserId(userId)
        UserSessionStore.setHomeRoleCode(selectedOption.roleCode)
        UserSessionStore.setHomeRoleSchoolId(selectedOption.schoolId)
        UserSessionStore.setHomeRoleSchoolCode(selectedOption.schoolCode)
        UserSessionStore.setActiveAssignmentId(selectedOption.id)
        scLog(
            "$TAG continueWithSelectedRole user=$userId role=${selectedOption.roleCode} school=${selectedOption.schoolId.ifBlank { "<blank>" }} code=${selectedOption.schoolCode.ifBlank { "<blank>" }}"
        )
        onAuthenticated(userId, selectedOption.roleCode, selectedOption.schoolId)
    }

    private fun buildRolePickerState(response: VerifyOtpResponse): RolePickerUiState {
        val options = response.identityContextProfiles.mapIndexed { index, profile ->
            profile.toRolePickerOption(index)
        }
        return RolePickerUiState(
            isVisible = false,
            title = "Choose Your Role",
            subtitle = "Select a role to continue",
            options = options,
            selectedOptionId = options.firstOrNull()?.id.orEmpty(),
            rememberSelection = true,
            canDismiss = true,
            isSubmitting = false
        )
    }

    private fun defaultRolePickerState(): RolePickerUiState {
        return RolePickerUiState(
            isVisible = false,
            title = "Choose Your Role",
            subtitle = "Select a role to continue",
            options = emptyList(),
            selectedOptionId = "",
            rememberSelection = true,
            canDismiss = true,
            isSubmitting = false
        )
    }

    private fun IdentityContextProfile.toRolePickerOption(index: Int): RolePickerOption {
        val schoolIdValue = schoolId.orEmpty()
        val schoolLabel = schoolName.orEmpty().ifBlank {
            if (schoolIdValue.isBlank()) "Platform Context" else "School Context"
        }
        val roleCode = resolveRoleCode()
        return RolePickerOption(
            id = roleAssignmentId.ifBlank { "profile-$index" },
            roleCode = roleCode,
            roleLabel = shellRoleLabel(roleCode),
            schoolId = schoolIdValue,
            schoolCode = "",
            schoolLabel = schoolLabel,
            isLastUsed = index == 0
        )
    }

    private fun derivePrimaryContext(response: VerifyOtpResponse): RolePickerOption {
        val firstProfile = response.identityContextProfiles.firstOrNull()
        return firstProfile?.toRolePickerOption(0) ?: defaultRolePickerState().options.first()
    }

    private fun emitError(message: String) {
        _uiState.update {
            it.copy(
                errorMessage = message,
                infoMessage = "",
                errorNonce = it.errorNonce + 1
            )
        }
    }

    private fun startCountdown(startSeconds: Int) {
        cancelCountdown()
        countdownJob = scope.launch {
            for (remaining in startSeconds downTo 1) {
                _uiState.update { it.copy(otpCountdownSeconds = remaining) }
                delay(1000)
            }
            _uiState.update {
                // The resend cooldown is independent from OTP validity. The
                // backend keeps the issued OTP valid for 10 minutes, so the
                // existing code must remain verifiable after the 60s cooldown.
                it.copy(otpCountdownSeconds = 0)
            }
        }
    }

    private fun cancelCountdown() {
        countdownJob?.cancel()
        countdownJob = null
    }

    fun clear() {
        cancelCountdown()
        scope.cancel()
    }
}
